// «سعود» — short HTML designs (action {type:'design', format:'image'|'pdf', table, columns, scope, title, html}).
// The model writes a SHORT template with placeholders only, never the data:
//   {{title}} {{school}} {{date_hijri}} {{date}} {{count}} {{logo}}   and   {{#rows}} … {{c0}} {{c3}} {{n}} … {{/rows}}
// Here: the template is sanitized again with a strict allow-list (the server already did), the placeholders are filled
// from the page's own rows as TEXT (never parsed as HTML), its CSS is scoped to an isolated off-screen container that
// carries the admin template font / colours (CSS variables --primary / --accent), and the result is rasterized to a
// PNG, or to a PDF whose pages are cut between rows (a row is never split).
import { prepare, templatesOf, todayLines, safeName, internals } from './actions';

var MAX_HTML = 20000;
var IMAGE_ROWS = 400;
var PDF_ROWS = 2000;

/* ---------------------------------------------------------------- sanitizer ------------------------------------- */

// removed with their content
var DROP = /^(script|iframe|object|embed|link|meta|base|form|input|button|textarea|select|option|optgroup|datalist|svg|math|video|audio|source|track|picture|frame|frameset|template|noscript|canvas|applet|portal|dialog|slot|title|head|area|map)$/i;
// kept (anything else is unwrapped: its text stays)
var KEEP = {};
('div span p br hr b strong i em u s small sub sup mark bdi bdo h1 h2 h3 h4 h5 h6 ul ol li dl dt dd table thead tbody tfoot ' +
  'tr th td caption colgroup col img section header footer article aside main figure figcaption blockquote q cite abbr ' +
  'time style center font big').split(' ').forEach(function(t){ KEEP[t] = 1; });
var ATTRS = { class: 1, style: 1, dir: 1, align: 1, valign: 1, colspan: 1, rowspan: 1, width: 1, height: 1, alt: 1, title: 1,
  lang: 1, border: 1, cellpadding: 1, cellspacing: 1, color: 1, bgcolor: 1, size: 1, 'data-sd-rows': 1, 'data-sd-row': 1 };
var DATA_IMG = /^data:image\/(png|jpe?g|gif|webp);base64,[a-z0-9+/=\s]+$/i;
var LOGO = /^\{\{\s*logo\s*\}\}$/;

// CSS: no imports / fonts from outside, no url() except inline images, no scripts, nothing fixed to the screen
export function cleanCss(css){
  return String(css || '')
    .replace(/\/\*[\s\S]*?\*\//g, '')
    .replace(/@import[^;]*;?/gi, '')
    .replace(/@(charset|namespace)[^;]*;?/gi, '')
    .replace(/@font-face\s*\{[^}]*\}/gi, '')
    .replace(/url\s*\(\s*(['"]?)(.*?)\1\s*\)/gi, function(m, q, u){ return /^data:image\/(png|jpe?g|gif|webp);base64,/i.test(u) ? m : 'none'; })
    .replace(/expression\s*\(/gi, '(').replace(/javascript\s*:/gi, '').replace(/vbscript\s*:/gi, '')
    .replace(/(-moz-binding|behavior)\s*:[^;}]*/gi, '')
    .replace(/position\s*:\s*(fixed|sticky)/gi, 'position:relative')
    .replace(/\{\{[^}]*\}\}/g, '');
}

function sanitize(root){
  var kids = Array.prototype.slice.call(root.childNodes);
  kids.forEach(function(n){
    if (n.nodeType == 3) return;
    if (n.nodeType != 1){ n.parentNode.removeChild(n); return; }        // comments, processing instructions
    var tag = n.tagName.toLowerCase();
    if (DROP.test(tag)){ n.parentNode.removeChild(n); return; }
    if (!KEEP[tag]){
      sanitize(n);
      while (n.firstChild) n.parentNode.insertBefore(n.firstChild, n);
      n.parentNode.removeChild(n);
      return;
    }
    Array.prototype.slice.call(n.attributes).forEach(function(a){
      var name = a.name.toLowerCase();
      if (name == 'src' && tag == 'img' && (DATA_IMG.test(a.value) || LOGO.test(a.value))) return;
      if (!ATTRS[name]){ n.removeAttribute(a.name); return; }
      if (name == 'style') n.setAttribute('style', cleanCss(a.value));
      else if (/javascript:|vbscript:|data:/i.test(a.value)) n.removeAttribute(a.name);
    });
    if (tag == 'img' && !n.getAttribute('src')){ n.parentNode.removeChild(n); return; }
    if (tag == 'style'){ n.textContent = cleanCss(n.textContent); return; }
    sanitize(n);
  });
  return root;
}

/* ---------------------------------------------------------------- template -------------------------------------- */

var SECTION = /\{\{\s*#\s*rows\s*\}\}([\s\S]*?)\{\{\s*\/\s*rows\s*\}\}/g;
var PH = /\{\{\s*([A-Za-z_][\w-]*)\s*\}\}/g;

function firstTag(html){
  var m = /^\s*<\s*([a-z0-9]+)/i.exec(html);
  return m ? m[1].toLowerCase() : '';
}

// a {{#rows}} block is replaced by a marker that the HTML parser keeps in its context (a <tr> inside a table …)
function markerTag(inner){
  var t = firstTag(inner);
  if (t == 'tr') return 'tr';
  if (t == 'td' || t == 'th') return 'td';
  if (t == 'li') return 'li';
  if (t == 'dt' || t == 'dd') return 'dd';
  return 'div';
}

function fragment(html){
  var tpl = document.createElement('template');
  tpl.innerHTML = html;
  return tpl.content;
}

// the inner part of {{#rows}}, parsed in its own context and sanitized
function rowTemplate(inner){
  var t = firstTag(inner), host;
  if (t == 'tr'){ host = fragment('<table><tbody>' + inner + '</tbody></table>').querySelector('tbody'); }
  else if (t == 'td' || t == 'th'){ host = fragment('<table><tbody><tr>' + inner + '</tr></tbody></table>').querySelector('tr'); }
  else if (t == 'li'){ host = fragment('<ul>' + inner + '</ul>').querySelector('ul'); }
  else host = fragment(inner);
  var out = document.createDocumentFragment();
  while (host && host.firstChild) out.appendChild(host.firstChild);
  sanitize(out);
  // every top-level element of a row: a PDF page break never falls inside it
  Array.prototype.forEach.call(out.childNodes, function(n){ if (n.nodeType == 1) n.setAttribute('data-sd-row', ''); });
  return out;
}

export function parseTemplate(html){
  var inners = [];
  var raw = String(html || '').slice(0, MAX_HTML).replace(SECTION, function(m, inner){
    var k = inners.length, tag = markerTag(inner);
    inners.push(inner);
    return '<' + tag + ' data-sd-rows="' + k + '"></' + tag + '>';
  });
  var outer = sanitize(fragment(raw));
  return { outer: outer, rows: inners.map(rowTemplate) };
}

// text from a value: line breaks become <br>
function textNodes(doc, value){
  var f = doc.createDocumentFragment();
  String(value == null ? '' : value).split('\n').forEach(function(line, i){
    if (i) f.appendChild(doc.createElement('br'));
    if (line) f.appendChild(doc.createTextNode(line));
  });
  return f;
}

// fills {{key}} in text nodes and attributes. Values go in as text (setAttribute / text nodes): nothing is parsed.
function fill(root, vals, logo){
  var doc = root.ownerDocument || document;
  var walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  var texts = [], els = [], n;
  while ((n = walker.nextNode())){
    if (n.nodeType == 3){ if (n.parentNode && n.parentNode.nodeName != 'STYLE' && /\{\{/.test(n.nodeValue)) texts.push(n); }
    else els.push(n);
  }
  els.forEach(function(el){
    if (el.tagName == 'IMG' && LOGO.test(el.getAttribute('src') || '')){
      if (logo) el.setAttribute('src', logo); else el.parentNode && el.parentNode.removeChild(el);
      return;
    }
    Array.prototype.slice.call(el.attributes).forEach(function(a){
      if (!/\{\{/.test(a.value) || a.name == 'src') return;
      var v = a.value.replace(PH, function(m, k){ return k == 'logo' ? '' : String(vals[k] == null ? '' : vals[k]).replace(/\n/g, ' '); });
      el.setAttribute(a.name, a.name == 'style' ? cleanCss(v) : v);
    });
  });
  texts.forEach(function(t){
    var s = t.nodeValue, f = doc.createDocumentFragment(), last = 0, m;
    PH.lastIndex = 0;
    while ((m = PH.exec(s))){
      if (m.index > last) f.appendChild(doc.createTextNode(s.slice(last, m.index)));
      if (m[1] == 'logo'){
        if (logo){ var img = doc.createElement('img'); img.setAttribute('src', logo); img.setAttribute('style', 'max-height:70px;max-width:120px'); f.appendChild(img); }
      } else f.appendChild(textNodes(doc, vals[m[1]]));
      last = m.index + m[0].length;
    }
    if (last < s.length) f.appendChild(doc.createTextNode(s.slice(last)));
    t.parentNode.replaceChild(f, t);
  });
}

// CSS of the template, every selector scoped to the container (html / body / :root = the container itself)
function scopeCss(css, scope){
  var d = document.implementation.createHTMLDocument('');
  var st = d.createElement('style');
  st.textContent = css;
  d.head.appendChild(st);
  var rules = (st.sheet && st.sheet.cssRules) || [];
  var one = function(sel){
    sel = sel.trim();
    if (!sel) return '';
    var root = /^(html|body|:root)\b/i.exec(sel);
    if (root) return scope + sel.slice(root[0].length);
    return scope + ' ' + sel;
  };
  var walk = function(list){
    var out = '';
    Array.prototype.forEach.call(list, function(r){
      if (r.type == 1) out += r.selectorText.split(',').map(one).filter(Boolean).join(', ') + '{' + r.style.cssText + '}';
      else if (r.type == 4) out += '@media ' + r.media.mediaText + '{' + walk(r.cssRules) + '}';
    });
    return out;
  };
  return walk(rules);
}

/* ---------------------------------------------------------------- render ---------------------------------------- */

var uidN = 0;

// builds the design element (not yet in the page) for rows
function build(action, table, p, ctx, t, W, logo){
  var id = 'sd-design-' + (++uidN);
  var tpl = parseTemplate(action.html);
  var d = todayLines();
  var common = {
    title: action.title || table.title || '', school: (ctx.school && ctx.school.name) || '',
    date_hijri: d.hijri, date: d.gregorian, count: String(p.rows.length),
  };
  // the fixed placeholders first: a cell that happens to contain «{{…}}» is then never filled again
  fill(tpl.outer, common, logo);
  // {{#rows}}: one copy per row, with {{c<n>}} = that row's cell (any column of the table) and {{n}} = its number
  Array.prototype.slice.call(tpl.outer.querySelectorAll('[data-sd-rows]')).forEach(function(marker){
    var rt = tpl.rows[+marker.getAttribute('data-sd-rows')];
    var host = marker.parentNode;
    p.rows.forEach(function(row, i){
      var vals = Object.assign({}, common, { n: String(i + 1), index: String(i + 1) });
      table.columns.forEach(function(c){ vals[c.key] = row[c.index] == null ? '' : row[c.index]; });
      var copy = rt.cloneNode(true);
      fill(copy, vals, logo);
      host.insertBefore(copy, marker);
    });
    host.removeChild(marker);
  });

  // the template's own CSS, scoped to the container
  var css = '';
  Array.prototype.slice.call(tpl.outer.querySelectorAll('style')).forEach(function(s){ css += s.textContent + '\n'; s.parentNode.removeChild(s); });

  var el = document.createElement('div');
  el.id = id;
  var S = '#' + id;
  var base =
    // the page's own CSS never reaches the design (reverted), the template's CSS (scoped, after) wins
    S + '{width:' + W + 'px;box-sizing:border-box;padding:28px;background:#fff;color:#1f2937;direction:rtl;text-align:right;' +
    'font-family:"Noto Kufi Arabic",Tahoma,Arial,sans-serif;font-size:' + t.font_size + 'px;line-height:1.7;letter-spacing:0;' +
    '--primary:' + t.primary + ';--accent:' + t.accent + '}' +
    S + ' *:not(style){all:revert;box-sizing:border-box;letter-spacing:0}' +
    // defaults in the admin template colours
    S + ' table{width:100%;border-collapse:collapse;margin:8px 0}' +
    S + ' th,' + S + ' td{border:1px solid #d6dbe1;padding:5px 8px;vertical-align:middle}' +
    S + ' th{background:' + t.primary + ';color:#fff;font-weight:700}' +
    S + ' h1,' + S + ' h2,' + S + ' h3{color:' + t.primary + ';margin:0 0 8px;line-height:1.5}' +
    S + ' p{margin:0 0 6px}' + S + ' img{max-width:100%}';
  el.innerHTML = '<style>' + base + scopeCss(cleanCss(css), S) + '</style>';
  el.appendChild(tpl.outer);
  return el;
}

// cut heights of a rendered design into pages without splitting a row
function cuts(el, pageH){
  var top = el.getBoundingClientRect().top;
  var rows = Array.prototype.map.call(el.querySelectorAll('[data-sd-row]'), function(r){
    var b = r.getBoundingClientRect();
    return [b.top - top, b.bottom - top];
  });
  var total = el.offsetHeight, y = 0, pages = [];
  while (y < total - 1 && pages.length < 400){
    var end = Math.min(total, y + pageH);
    if (end < total){
      for (var guard = 0; guard < 60; guard++){
        var moved = false;
        for (var i = 0; i < rows.length; i++){
          if (rows[i][0] < end - 0.5 && rows[i][1] > end + 0.5 && rows[i][0] > y + 0.5){ end = rows[i][0]; moved = true; }
        }
        if (!moved) break;
      }
      if (end <= y + 20) end = Math.min(total, y + pageH);     // one row taller than a page: it has to be cut
    }
    pages.push([y, end]);
    y = end;
  }
  return pages;
}

// → Promise<{ok, message, preview?, file?: {kind: 'image'|'pdf', blob, name}}>
export function makeDesign(action, table, ctx){
  if (!action.html || typeof action.html != 'string') return Promise.resolve({ ok: false, message: 'التصميم فارغ. اطلب من سعود تصميماً جديداً.' });
  if (action.html.length > MAX_HTML) return Promise.resolve({ ok: false, message: 'التصميم أطول من المسموح.' });
  var isPdf = action.format == 'pdf';
  var p = prepare({ columns: action.columns, filter: action.filter, sort: action.sort }, table);
  if (!p.ok) return Promise.resolve(p);
  var all = p.rows.length, cap = isPdf ? PDF_ROWS : IMAGE_ROWS, cut = all > cap;
  if (cut) p.rows = p.rows.slice(0, cap);
  var t = templatesOf(ctx.templates);
  var paper = internals.PAPER[t.paper] ? t.paper : 'A4';
  var W = internals.PAPER[paper][0], H = internals.PAPER[paper][1];
  var progress = ctx.onProgress || function(){};
  var wrap = null;
  progress('جاري تجهيز التصميم…');
  return Promise.all([internals.loadCanvasLibs(isPdf), internals.logoUrl(t, ctx.school), document.fonts && document.fonts.ready ? document.fonts.ready.catch(function(){}) : null])
    .then(function(res){
      var libs = res[0];
      var el = build(action, table, p, ctx, t, W, res[1]);
      wrap = internals.stage(W);
      wrap.appendChild(el);
      internals.wordSpans(el);
      var h = el.offsetHeight;
      var name = safeName(action.title || table.title);
      var note = cut ? ' أول ' + cap + ' صف من ' + all + '.' : '';
      if (!isPdf){
        var scale = h * 2 <= 30000 ? 2 : 1;
        progress('جاري رسم الصورة…');
        return internals.snap(libs.h2c, el, scale).then(function(canvas){
          return new Promise(function(resolve){ canvas.toBlob(function(b){ resolve({ blob: b, canvas: canvas }); }, 'image/png'); });
        }).then(function(x){
          internals.download(x.blob, name + '.png');
          return { ok: true, message: 'تم حفظ التصميم (' + p.rows.length + ' صف).' + note, preview: internals.thumb(x.canvas),
            file: { kind: 'image', blob: x.blob, name: name + '.png' } };
        });
      }
      var M = 28, FOOT = 30;
      var pages = cuts(el, H - M * 2 - FOOT);
      // one drawing when it fits a canvas (whole-number scale), else one per page
      var scale = h * 2 <= 30000 ? 2 : h <= 30000 ? 1 : 0;
      var whole = scale ? internals.snap(libs.h2c, el, scale) : Promise.resolve(null);
      var s = scale || 2;
      var pdf = new libs.jsPDF({ orientation: paper == 'A4' ? 'p' : 'l', unit: 'pt', format: 'a4', compress: true });
      var pw = internals.PT[paper][0], ph = internals.PT[paper][1];
      var shrink = pages.length > 30 ? 0.65 : pages.length > 10 ? 0.8 : 1;
      return whole.then(function(full){
        var i = 0;
        function next(){
          if (i >= pages.length){
            var blob = pdf.output('blob');
            internals.download(blob, name + '.pdf');
            return { ok: true, message: 'تم تحميل التصميم PDF (' + p.rows.length + ' صف، ' + pages.length + (pages.length == 1 ? ' صفحة' : ' صفحات') + ').' + note,
              file: { kind: 'pdf', blob: blob, name: name + '.pdf' } };
          }
          var k = i, y0 = pages[k][0], y1 = pages[k][1];
          progress('جاري تجهيز الصفحة ' + (k + 1) + ' من ' + pages.length + '…');
          var part = full ? Promise.resolve(full) : internals.snapCrop(libs.h2c, el, s, y0, y1 - y0);
          return part.then(function(src){
            var c = document.createElement('canvas');
            c.width = W * s; c.height = H * s;
            var x = c.getContext('2d');
            x.fillStyle = '#fff'; x.fillRect(0, 0, c.width, c.height);
            var sy = full ? y0 * s : 0;
            x.drawImage(src, 0, sy, W * s, (y1 - y0) * s, 0, M * s, W * s, (y1 - y0) * s);
            // footer drawn on the canvas (canvas text joins Arabic letters correctly)
            x.fillStyle = '#6b7280';
            x.font = (10 * s) + 'px "Noto Kufi Arabic", Tahoma, sans-serif';
            x.direction = 'rtl'; x.textAlign = 'right';
            x.fillText(t.footer || '', (W - M) * s, (H - 14) * s);
            x.direction = 'rtl'; x.textAlign = 'left';
            x.fillText('صفحة ' + (k + 1) + ' من ' + pages.length, M * s, (H - 14) * s);
            if (k > 0) pdf.addPage('a4', paper == 'A4' ? 'p' : 'l');
            pdf.addImage(internals.resized(c, shrink).toDataURL('image/jpeg', 0.85), 'JPEG', 0, 0, pw, ph, undefined, 'FAST');
            i++;
            return next();
          });
        }
        return next();
      });
    })
    .then(function(r){ if (wrap) wrap.remove(); return r; }, function(e){
      if (wrap) wrap.remove();
      console.error('[saud] design', e);
      return { ok: false, message: 'تعذّر تجهيز التصميم في المتصفح. اطلب جدولاً أو ملف PDF عادياً.' };
    });
}
